import "dayjs/locale/ja";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/types";
import { Button, Checkbox, Col, DatePicker, Form, Input, InputNumber, Row, Select } from "antd";
import validateMessages from "../../../utils/validate";
import { Rule } from "antd/es/form";
import { useTranslation } from "react-i18next";
import { DateFormat, VoucherCategories, VoucherModules, VoucherTypes } from "../../../shared/constants";
import dayjs from "dayjs";
import hrnUtils from "../../../utils/hrnUtils";
import AccountList from "../../../components/AccountList";
import Icon from "../../../components/Icon";
import { useCreateVoucherMutation, useLazyGetNewVoucherNoQuery, useLazyGetVoucherQuery, useUpdateVoucherMutation } from "../../../services/voucher";
import { useEffect, useState } from "react";
import { MinusCircleOutlined } from "@ant-design/icons";
import { useLazyGetVoucherCustomFieldsQuery, useLazyGetVoucherFormCustomFieldsQuery, useLazyGetVoucherTagsQuery, useSaveVoucherCustomFieldsMutation } from "../../../services/misc";
import { useNavigate, useParams } from "react-router-dom";
import ROUTES from "../../../routes";
import Notification from "../../../components/Notification";
import HrnModal from "../../../components/HrnModal";

const CreditVoucher = () => {
  const { voucherId } = useParams();
  const formName: string = "CreditVoucher";
  const navigate = useNavigate();
  const { loggedInUser } = useSelector((state: RootState) => state.global);
  const { t } = useTranslation();
  const [voucherForm] = Form.useForm();
  const [fieldsForm] = Form.useForm();
  const requiredRule: Rule = { required: true, message: "Required" };
  const companyId: string = loggedInUser?.currentCompany?.id || "";
  const voucherBasic = {
    voucherTypeId: VoucherTypes.CreditVoucher,
    module: VoucherModules.CreditVoucher,
    category: VoucherCategories.GeneralVoucher
  };
  const [currentVoucherId, setCurrentVoucherId] = useState<any>(voucherId);
  const [voucherDate, setVoucherDate] = useState(dayjs());
  const [transAmount, setTransAmount] = useState(0);
  const [voucherFormFields, setVoucherFormFields] = useState([]);
  const [getNewVoucherNo] = useLazyGetNewVoucherNoQuery();
  const [getVoucher, getVoucherResult] = useLazyGetVoucherQuery();
  const [getVoucherCustomfields, getVoucherCustomfieldsResult] = useLazyGetVoucherCustomFieldsQuery();
  const [getVoucherFormCustomfields, getVoucherFormCustomfieldsResult] = useLazyGetVoucherFormCustomFieldsQuery();
  const [getVoucherTags, getVoucherTagsResult] = useLazyGetVoucherTagsQuery();
  const [createVoucher, createVoucherResult] = useCreateVoucherMutation();
  const [updateVoucher, updateVoucherResult] = useUpdateVoucherMutation();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [saveVoucherFields, saveVoucherFieldsResult] = useSaveVoucherCustomFieldsMutation();

  const amountChanged = () => {
    const transAccounts = voucherForm.getFieldValue("transAccounts") || [];
    const total = transAccounts.reduce((sum: number, item: any) => sum + (parseFloat(item.amount || 0) || 0), 0);
    setTransAmount(total);
  };

  useEffect(() => {
    getVoucherTags({ companyId }).unwrap();
  }, []);

  useEffect(() => {
    let formValues: any = {
      id: null,
      transAccounts: [{ accountId: null, amount: null }]
    };

    if (currentVoucherId) {
      getVoucher({ companyId, id: currentVoucherId, withDetail: false }).unwrap().then((data: any) => {
        const accounts = data.transAccounts || [];
        const vfFields = (data.voucherFields || []).map((fld: any) => ({
          ...fld, value: fld.value && fld.type === "Date" ? dayjs(fld.value) : fld.value
        }));
        setVoucherFormFields(vfFields);
        formValues = {
          ...data,
          id: currentVoucherId,
          transTime: dayjs(data.transTime),
          tags: data.tags || [],
          collectToAccTxId: accounts.find((acc: any) => acc.debitAmount > 0)?.id,
          collectToAccountId: accounts.find((acc: any) => acc.debitAmount > 0)?.accountId,
          transAccounts: accounts.filter((acc: any) => acc.creditAmount > 0).map((acc: any) => ({ id: acc.id, accountId: acc.accountId, amount: acc.creditAmount })),
          voucherFields: vfFields
        };
        voucherForm.setFieldsValue(formValues);
        amountChanged();
      });
    } else {
      voucherForm.setFieldsValue(formValues);
    }

    getVoucherFormCustomfields({ companyId, form: formName }).unwrap().then((formFields: any) => {
      formValues.voucherFields = formFields || [];
      getVoucherCustomfields({ companyId }).unwrap().then((fields: any[]) => {
        fieldsForm.setFieldsValue({
          voucherFields: (fields || []).map((x: any) => ({ checked: formFields.findIndex((f: any) => f.name === x.name) !== -1, ...x }))
        });
      });
      if (currentVoucherId) {
        const vfieldValues = (voucherFormFields || []).map((fld: any) => ({
          ...fld, value: fld.value
        }));
        const voucherFields = (formFields || []).map((item: any) => ({ ...item, value: vfieldValues.find((x: any) => x.name === item.name)?.value }));
        voucherFields.push(...vfieldValues.filter((x: any) => voucherFields.findIndex((v: any) => v.name === x.name) === -1))

        voucherForm.setFieldValue("voucherFields", voucherFields);
        amountChanged();
      }
    });
  }, [currentVoucherId]);

  const getNewVoucherNumber = () => {
    if (currentVoucherId) return;
    const dt = voucherDate ? dayjs(voucherDate).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");
    getNewVoucherNo({ companyId, voucherTypeId: voucherBasic.voucherTypeId, voucherDate: dt }).unwrap().then((res) =>
      voucherForm.setFieldValue("voucherNo", res.voucherNo));
  };

  useEffect(() => {
    getNewVoucherNumber();
    const intervalId = setInterval(() => {
      getNewVoucherNumber();
    }, 10000);
    return () => clearInterval(intervalId);
  }, []);

  const voucherDateChanged = (value: any) => {
    setVoucherDate(value);
    setTimeout(getNewVoucherNumber, 0);
  };
  const postVoucher = async () => {
    try {
      await voucherForm.validateFields();
    } catch (errorInfo) {
      return;
    }

    const formFields = voucherForm.getFieldsValue();
    const transAccounts = [
      { id: formFields.collectToAccTxId, accountId: formFields.collectToAccountId, debitAmount: transAmount, creditAmount: 0 },
      ...(formFields.transAccounts || []).map((x: any) => ({ id: x.id, accountId: x.accountId, debitAmount: 0, creditAmount: x.amount || 0 }))
    ];
    const payload: any = {
      ...voucherBasic,
      id: formFields.id,
      transTime: formFields.transTime,
      voucherNo: formFields.voucherNo,
      bearer: formFields.bearer,
      description: formFields.description,
      tags: formFields.tags || [],
      transAccounts,
      voucherFields: formFields.voucherFields || []
    };
    try {
      if (currentVoucherId) {
        await updateVoucher({ companyId, id: currentVoucherId, body: payload }).unwrap();
      } else {
        await createVoucher({ companyId, body: payload }).unwrap();
      }
      voucherForm.resetFields();
      setCurrentVoucherId(null);
      Notification({
        type: "success",
        title: t("Credit Voucher"),
        description: t("The voucher has been saved successfully"),
        placement: "top"
      });
      navigate(ROUTES.PATHS.CREDIT_VOUCHER_MENU);
    } catch (e) { }
  };

  const showSettingsModal = () => {
    setSettingsOpen(true);
  };
  const hideSettingsModal = () => {
    setSettingsOpen(false);
  };
  const saveSettings = async () => {
    try {
      await fieldsForm.validateFields();
    } catch (errorInfo) {
      return;
    }

    const formFields = fieldsForm.getFieldsValue();
    const payload = {
      form: formName,
      fieldNames: formFields.voucherFields.filter((x: any) => x.checked === true).map((x: any) => x.name)
    };
    try {
      await saveVoucherFields({ companyId, body: payload }).unwrap();
    } catch { }
    fieldsForm.resetFields();
    setSettingsOpen(false);
  };

  return (
    <div className="credit-voucher-posting pl-[15px] pr-[15px]">
      <Form
        form={voucherForm}
        layout="horizontal"
        name="voucherForm"
        validateMessages={validateMessages}
        disabled={getVoucherFormCustomfieldsResult.isLoading || getVoucherResult.isLoading || createVoucherResult.isLoading || updateVoucherResult.isLoading}
      >
        <Row>
          <Col className="w-[60%] voucher-basic-fields">
            <Row>
              <Col className="w-[50%] pr-5">
                <Form.Item
                  name="id"
                  style={{ display: "none" }}
                  rules={[{ required: !!currentVoucherId }]}
                >
                  <Input placeholder={"Voucher No."} disabled />
                </Form.Item>
                <Form.Item name="transTime"
                  label={<span className="w-[150px] text-left">{t("Voucher Date")}<span style={{ color: "red", marginLeft: "2px" }}>*</span></span>}
                  rules={[requiredRule]}
                  initialValue={dayjs()}
                >
                  <DatePicker allowClear={false} placeholder={t("Select date")} className="w-full" format={DateFormat.USUAL} disabledDate={(date: any) => date > dayjs()} onChange={voucherDateChanged} onKeyDown={(e: any) => hrnUtils.handleDateFieldKeyDownEvent(e)} />
                </Form.Item>
              </Col>
              <Col className="w-[50%]">
                <Form.Item
                  name="voucherNo"
                  label={<span className="w-[150px] text-left">{t("Voucher No.")}<span style={{ color: "red", marginLeft: "2px" }}>*</span></span>}
                  rules={[requiredRule]}
                >
                  <Input placeholder={"Voucher No."} disabled />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col className="w-[100%]">
                <Form.Item name={"collectToAccTxId"} style={{ display: "none" }}>
                  <Input />
                </Form.Item>
                <Form.Item name="collectToAccountId"
                  label={<span className="w-[150px] text-left">{t("Collected to Account")}<span style={{ color: "red", marginLeft: "2px" }}>*</span></span>}
                  rules={[requiredRule]}
                >
                  <AccountList companyId={companyId} placeholder="Select Dr Account" />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col className="w-[100%]">
                <Form.Item name="bearer" label={<span className="w-[150px] text-left" >{t("Bearer")}</span>} >
                  <Input placeholder={"Bearer"} />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col className="w-[100%]">
                <fieldset className="mb-4" style={{ minHeight: "250px" }}>
                  <legend style={{ width: "auto", border: "none", padding: "0 5px", marginBottom: "10px" }}>Debit Accounts</legend>
                  <Form.List name="transAccounts">
                    {(fields, { add, remove }) => (
                      <>
                        <div className="trans-account">
                          {fields.map(({ key, name, ...restField }) => (
                            <div key={key} className="flex items-center flex-wrap md:flex-none gap-x-1 w-[100%]">
                              <Form.Item name={[name, "id"]} style={{ display: "none" }}>
                                <Input />
                              </Form.Item>
                              <Form.Item
                                {...restField}
                                name={[name, "accountId"]}
                                rules={[requiredRule]}
                                style={{ width: "calc(80% - 25px", marginBottom: "5px" }}
                              >
                                <AccountList companyId={companyId} placeholder="Select Cr Account" />
                              </Form.Item>
                              <Form.Item
                                {...restField}
                                name={[name, "amount"]}
                                className="w-[20%]"
                                style={{ marginBottom: "5px" }}
                                rules={[requiredRule]}
                              >
                                <InputNumber type="number" inputMode="decimal" style={{ width: "100%", textAlign: "right" }} placeholder={"Amount"} onChange={amountChanged} />
                              </Form.Item>
                              <MinusCircleOutlined style={{ width: "15px" }}
                                className="hidden sm:block mt-1 mb-2"
                                onClick={() => remove(name)}
                              />
                            </div>
                          ))}
                        </div>
                        <Form.Item className="mt-1" style={{ marginBottom: "0.25rem" }}>
                          <Button
                            ghost
                            type="primary"
                            onClick={add}
                            className="float-left"
                          >
                            <div className="flex items-center gap-1.5">
                              <Icon icon="add" className="h-4.5 w-4.5" />
                              <div>{"Add Cr Account"}</div>
                            </div>
                          </Button>
                          <label className="float-right mr-[25px]">{transAmount}</label>
                        </Form.Item>
                      </>
                    )}
                  </Form.List>
                </fieldset>
              </Col>
            </Row>
          </Col>
          <Col className="w-[40%] voucher-extra-fields">
            {getVoucherFormCustomfieldsResult.isSuccess && <Form.List name="voucherFields" >
              {(fields, { add, remove }) => (
                <>
                  <div className="custom-field pl-[15px]" style={{ maxHeight: "400px", overflow: "auto" }}>
                    {fields.map(({ key, name, ...restField }) => (
                      <div key={key} className="flex items-center flex-wrap md:flex-none gap-x-1 w-[100%]">
                        <Form.Item name={[name, "name"]} rules={[requiredRule]} style={{ display: "none" }}>
                          <Input />
                        </Form.Item>
                        <Form.Item name={[name, "displayName"]} rules={[requiredRule]} style={{ display: "none" }}>
                          <Input />
                        </Form.Item>
                        <Form.Item name={[name, "type"]} rules={[requiredRule]} style={{ display: "none" }}>
                          <Input />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, "value"]}
                          label={<span className="w-[150px] text-left pl-[10px]">{voucherForm.getFieldValue("voucherFields")?.[key]?.displayName}</span>}
                          style={{ marginBottom: "2px" }}
                        >
                          {voucherForm.getFieldValue("voucherFields")?.[key]?.type === "TextBox" && <Input className="w-[170px]" />}
                          {voucherForm.getFieldValue("voucherFields")?.[key]?.type === "Date" && <DatePicker className="w-[170px]" format={DateFormat.USUAL} disabledDate={(date: any) => date > dayjs()} onKeyDown={(e: any) => hrnUtils.handleDateFieldKeyDownEvent(e)} />}
                          {voucherForm.getFieldValue("voucherFields")?.[key]?.type === "DropDownList" && <Select style={{ width: "170px" }} options={getVoucherFormCustomfieldsResult?.data?.[key]?.params?.split("|")?.map((x: string) => ({ label: x, value: x }))} />}
                          {voucherForm.getFieldValue("voucherFields")?.[key]?.type === "Integer" && <InputNumber type="number" inputMode="numeric" onKeyPress={(e) => hrnUtils.handleNumberFieldKeyPressEvent(e)} style={{ width: "170px", textAlign: "right" }} />}
                          {voucherForm.getFieldValue("voucherFields")?.[key]?.type === "Decimal" && <InputNumber type="number" inputMode="decimal" onKeyPress={(e) => hrnUtils.handleNumberFieldKeyPressEvent(e, { decimal: true })} style={{ width: "170px", textAlign: "right" }} />}
                        </Form.Item>
                      </div>
                    ))}
                  </div>
                  <div className="custom-field-setting pl-[5px] mb-[5px] mt-5px">
                    <Button type="link" onClick={showSettingsModal}>
                      <div className="flex items-center gap-1.5">
                        <Icon icon="settings" className="h-4.5 w-4.5" />
                        <span>{t("Customized fields for this form")}</span>
                      </div>
                    </Button>
                  </div>
                </>
              )}
            </Form.List>}
          </Col>
        </Row>
        <Row>
          <Col className="w-[100%]">
            <Form.Item name="description" label={<span className="w-[150px] text-left">{t("Description")}</span>} >
              <Input.TextArea placeholder={"Description"} />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col className="w-[100%]">
            <Form.Item name="tags" label={<span className="w-[150px] text-left">{t("Tags")}</span>} >
              <Select
                mode="tags"
                style={{ width: "100%" }}
                placeholder="Tags for the voucher"
                loading={getVoucherTagsResult.isLoading}
                options={getVoucherTagsResult.isSuccess && getVoucherTagsResult.data.map((x: any) => ({ label: x, value: x }))}
              >
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row className="gap-3">
          <Button type="primary" size="large" onClick={postVoucher} >
            <div className="flex items-center gap-1.5">
              <Icon icon="edit" className="h-4.5 w-4.5" />
              <div>{t("Post Voucher")}</div>
            </div>
          </Button>
          <Button type="default" size="large" >
            <div className="flex items-center gap-1.5">
              <Icon icon="delete" className="h-4.5 w-4.5" />
              <div>{t("Delete Voucher")}</div>
            </div>
          </Button>
        </Row>
      </Form>
      <Form
        form={fieldsForm}
        layout="horizontal"
        name="fieldsForm"
        validateMessages={validateMessages}
      >
        <HrnModal
          open={settingsOpen}
          title={t("Voucher fields customization")}
          width={520}
          isLoading={getVoucherCustomfieldsResult.isLoading || saveVoucherFieldsResult.isLoading}
          onCancel={hideSettingsModal}
          onSave={saveSettings}
        >
          {getVoucherCustomfieldsResult.isSuccess && <Form.List name="voucherFields" >
            {(fields, { add, remove }) => (
              <>
                <div className="custom-field pl-[15px]" style={{ maxHeight: "400px", overflow: "auto" }}>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} className="flex items-center flex-wrap md:flex-none gap-x-1 w-[100%]">
                      <Form.Item name={[name, "checked"]} valuePropName="checked" className="w-[20px] !mb-1">
                        <Checkbox />
                      </Form.Item>
                      <Form.Item name={[name, "name"]} rules={[requiredRule]} className="w-[120px] !mb-1">
                        <Input disabled />
                      </Form.Item>
                      <Form.Item name={[name, "displayName"]} rules={[requiredRule]} className="w-[calc(100% - 220px)] !mb-1">
                        <Input disabled className="!text-black !bg-white" />
                      </Form.Item>
                      <Form.Item name={[name, "type"]} rules={[requiredRule]} className="w-[80px] !mb-1">
                        <Input disabled className="!text-black !bg-white" />
                      </Form.Item>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Form.List>}
        </HrnModal>
      </Form>
    </div >
  );
};

export default CreditVoucher;